import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StickyNote, Plus, Trash2, Loader2 } from "lucide-react";

const SLATE = "#475569";

export default function OfficeNoteTypes() {
  const [types, setTypes] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.officeNoteTypes().then(setTypes).catch(() => setTypes([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim()) { toast.error("Enter a note type."); return; }
    setBusy(true);
    try { await api.officeNoteTypeAdd(label.trim()); setLabel(""); toast.success("Added."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add."); }
    finally { setBusy(false); }
  };
  const del = async (t) => {
    if (!window.confirm(`Remove the "${t.label}" note type? Existing notes keep their label.`)) return;
    try { await api.officeNoteTypeDelete(t.id); toast.success("Removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove."); }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="office-note-types-page">
      <div className="flex items-center gap-2.5">
        <StickyNote className="h-5 w-5" style={{ color: SLATE }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Office Note Types</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-5">These are the categories office staff can pick when adding a note on a Staff Profile's Office Notes tab.</p>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex gap-2 mb-4">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="New note type (e.g. Compliment)" className="h-9 text-sm" data-testid="note-type-input" />
          <Button size="sm" className="h-9 shrink-0" style={{ backgroundColor: SLATE }} onClick={add} disabled={busy} data-testid="note-type-add">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}Add
          </Button>
        </div>

        {types === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : types.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">No note types yet — add one above.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="note-types-list">
            {types.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2.5" data-testid={`note-type-${t.id}`}>
                <span className="text-sm font-medium text-slate-700">{t.label}</span>
                <button onClick={() => del(t)} className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50" title="Remove" data-testid={`note-type-del-${t.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
